

import { MarkerClusterer } from '@googlemaps/markerclusterer';
import $ from 'jquery';

import LabelHelper from '../helpers/label-helper';

//import SnazzyInfoWindow from 'snazzy-info-window';

/**
 * CommunityMap Class
 * 
 * Purpose:     To contain various helper methods regarding Google Map functions
 */

/** INTENDED TO WORK WITH THE FOLLOWING JSON FOR COMMUNITY
* {
*      "regions": [
*          {
*              "region_name": <string>,
*              "communities": [
*                  {
*                      "community_name": <string>,
*                      "brand": "shea"|"trilogy"
*                      "lat": <number>,
*                      "long": <number>,
*                      "city": <string>,
*                      "state": <string>,
*                      "bedrooms_min": <number>,
*                      "bedrooms_max": <number>,
*                      "bathrooms_min": <number>,
*                      "bathrooms_max": <number>,
*                      "price_min": <number>,
*                      "price_max": <number>,
*                      "squarefeet_min": <number>,
*                      "squarefeet_max": <number>,
*                      "stories_min": <number>,
*                      "stories_max": <number>,
*                      "url": <String>,
*                      "types": [ "type1", "type2", ...,"typeX" ],
*                      "features": [ "feature1", "feature2", ..., "featureY" ]
*                  },
*                  ...,
*                  {}
*              ]
*          } 
*      ]
* }
*/

/**
* CommunityMap Class
* 
* **NOTE:** This is the refactored and updated version of the legacy `MapHelper.js`
* 
* @description To contain various helper methods regarding Google Map functions
* 
*/
var CommunityMap = {
    IS_LOCALHOST: location.host.indexOf("localhost") >= 0,
    // FYH Search Results Map Properties
    _map: null,
    _markers: [],
    _activeCommunityPopups: null,
    _communityPopups: null,
    _clusterer: null,
    _clustererHoverListener: null,
    _initialZoom: 0,                    // records the initial zoom determined by the clustering/map fitbounds algorithm so that all markers are visible
    _initialCenterPosition: null,               // records the initial center determined by the clustering/map fitbounds algorithm so that all markers are visible
    _lastZoom: 0,                // records the latest zoom from any dynamic map/cluster or user action
    _lastCenterPosition: null,           // records the latest center from any dynamic map/cluster or user action
    _numResultsDisplayed: 0,        // number of results currently displayed in the results section
    _communityGuidsSorted: [],  // stores a sorted list of communit guids according to the current 
    _visibleCommunityGuids: [], // list (unsorted) of visible communitys in the map viewport
    _communityLabels: [],
    MAX_ZOOM: 16,
    DEFAULT_INITIAL_POSITION_LAT: 39.50, // Lat and Long of center of usa
    DEFAULT_INITIAL_POSITION_LONG: -98.35, // Lat and Long of center of usa
    DEFAULT_INITIAL_ZOOM: 4,    // default inital zoom of map before data is loaded
    CLUSTER_GRID_SIZE: 100,
    RESULTS_LISTING_BATCH_SIZE: 8,
    /**
     * 
     * @param {*} mapElem 
     */
    createMap: function (mapElem) {

    },
    /**
     * Clear markers from map. See: * https://developers.google.com/maps/documentation/javascript/examples/marker-remove
     * 
     * @param {Google Map Marker array} markers List of markers to remove from map
     */
    clearMarkers: function (markers) {
        markers.forEach(function (marker) {
            marker.setMap(null);
        });
    },
    /**
     * @param {MarkerClusterer} markerClusterer MarkerClusterer instance to replace markers from
     * @param {Marker[]} markers - Array of Marker instances to add to the clusterer after removing all previous markers
     */
    setClusterMarkers: function (markerClusterer, markers) {
        markerClusterer.clearMarkers();
        markerClusterer.addMarkers(markers);
    },
    /**
     * Records the center and zoom of the map to later recenter the map to.
     * Intended to be invoked after map finishes rendering markers for filtered data.
     * 
     * NOTE: this is used to record the center and zoom level computed by the map
     * after clustering is finished so that all communities are visible in the viewport
     * @param {google.maps.LatLng} newCenter 
     * @param {int} newZoom 
     */
    setMapCenterAndZoom: function (newCenter, newZoom) {
        CommunityMap._initialCenterPosition = newCenter;
        CommunityMap._initialZoom = newZoom;
    },
    /**
     * Updates the map to move to the previously recorded center and zoom level
     * 
     * NOTE: this is used to record the center and zoom level computed by the map
     * after clustering is finished so that all communities are visible in the viewport
     */
    resetMapCenterAndZoom: function () {
        CommunityMap._map.setCenter(CommunityMap._initialCenterPosition);
        CommunityMap._map.setZoom(CommunityMap._initialZoom);
    },
    /**
     * Records the center and zoom of the map so that it can be restored later
     * 
     * NOTE: Intended to be invoked at the end of map and cluster drawing functions and 
     * user interactive actions so that this center and zoom setting can be restored later
     * @param {google.maps.LatLng} lastCenter 
     * @param {int} lastZoom 
     */
    setLastMapCenterAndZoom: function (lastCenter, lastZoom) {
        CommunityMap._lastCenterPosition = lastCenter;
        CommunityMap._lastZoom = lastZoom;
    },
    /**
     * Restores the last recorded center and zoom of the map so that it can be restored later
     * 
     * NOTE: Intended to be invoked at the end of map and cluster drawing functions and 
     * user interactive actions so that this center and zoom setting can be restored later
     */
    snapToLastMapCenterAndZoom: function () {
        CommunityMap._map.setCenter(CommunityMap._lastCenterPosition);
        CommunityMap._map.setZoom(CommunityMap._lastZoom);
    },
    /**
     * Create labels for each marker and cluster
     * @param {*} map 
     * @param {*} markerClusterer 
     */
    createCommunityLabels: function (map, markers, markerClusterer) {
        const _THIS = this;
        // Require Snazzy Maps now that Google Maps has loaded
        const SnazzyInfoWindow = require('snazzy-info-window')

        // close all existing info windows and reset fyhIconLabels variable
        if (this._communityLabels.length > 0) {
            for (var x = 0; x < this._communityLabels.length; x++) {
                this._communityLabels[x].close();
            }
            this.fyhIconLabels = [];
        }

        // use the get clusters method which returns an array of objects 
        // to create info window for each marker and cluster
        //var clusters = markerClusterer.getClusters(); 
        var clusters = markerClusterer.clusters;

        for (var i = 0, l = clusters.length; i < l; i++) {
            var cluster = clusters[i];
            var markers = cluster.markers;
            var numMarkersInCluster = markers.length;

            // cluster case
            if (numMarkersInCluster > 1) {
                // build string compilation of all marker's guids delimited by "|"
                var markerGuids = "";
                markers.forEach(function (marker, index) {
                    markerGuids += marker.customInfo.nodeGuid;
                    if (index < numMarkersInCluster - 1) {
                        markerGuids += "|";
                    }
                });
                // This is a cluster of multiple markers...create icon label for first marker in it
                var marker = markers[0];
                //marker.setMap(this.fyhSearchMap)
                if (marker != null) {

                    var markerLat = marker.position.lat();
                    var markerLng = marker.position.lng();

                    var iconLabel = new SnazzyInfoWindow({
                        map: map,
                        position: { lat: markerLat, lng: markerLng },
                        content: `<div class='fyh-search-icon-label cluster-label' data-node-guid='` + markerGuids + `'>
                                    <p class='icon-label-title'>` + numMarkersInCluster + ` Communities</p>` +
                            `</div>`,
                        placement: "bottom",        // where to place window with respect to marker
                        wrapperClass: "fyh-search-icon-label-wrapper cluster-label-wrapper data-node-guid-" + markerGuids,
                        openOnMarkerClick: false,    // open map on marker click
                        closeOnMapClick: false,      // close window on outside clicks on map
                        closeWhenOthersOpen: false,  // make sure only one window open at a time
                        showCloseButton: false,      // show close button
                        panOnOpen: false,            // make map pan to not clip window
                        padding: "0px",
                        border: true,              // remove border
                        callbacks: {
                            afterOpen: CommunityMap.initCommunityLabelHandlers
                        },

                    });

                    // Open the info window and save it
                    iconLabel.open();

                    this._communityLabels.push(iconLabel);
                }

            }
            // non-cluster case
            else {
                var marker = markers[0];
                marker.setMap(this._map);

                // Create icon label for this non clustered marker
                var iconLabel = new SnazzyInfoWindow({
                    marker: marker,             // marker associated with this info window
                    content: `<div class='fyh-search-icon-label' data-node-guid='` + marker.customInfo.nodeGuid + `'>
                                <p class='icon-label-title'>` + marker.customInfo.name + `</p>` +
                        `</div>`,
                    placement: "bottom",        // where to place window with respect to marker
                    wrapperClass: "fyh-search-icon-label-wrapper data-node-guid-" + marker.customInfo.nodeGuid,
                    openOnMarkerClick: false,    // open map on marker click
                    closeOnMapClick: false,      // close window on outside clicks on map
                    closeWhenOthersOpen: false,  // make sure only one window open at a time
                    showCloseButton: false,      // show close button
                    panOnOpen: false,            // make map pan to not clip window
                    padding: "0px",
                    border: true,              // remove border
                    callbacks: {
                        afterOpen: CommunityMap.initCommunityLabelHandlers
                    },
                });

                // Open the info window and save it
                iconLabel.open();

                this._communityLabels.push(iconLabel);
            }
        }
    },
    /**
     * Format the Pricing string  
     * @param {any} marker
     * @param {any} window
     * @param {any} tooltipIconUrl
     * @returns
     */
    formatPriceHtml: function (marker, window, tooltipIconUrl) {
        if (marker.customInfo.status === "Closing Out") {
            return "";
        }
        else if (window.IS_LOCALHOST) {
            return ` ${marker.customInfo.pricedFrom}<button data-tooltip data-click-open="true" class="pricing-tooltip top" data-disable-for-touch="false"
                                title="Pricing as of last sales release and is subject to change without notice"
                                data-title="Pricing as of last sales release and is subject to change without notice">
                                <!-- alt text REQUIRED -->
                                <img class="tooltip-v2" src="${tooltipIconUrl}"
                                    alt="More Info">
                            </button> `;
        }
        else {
            return marker.customInfo.pricedFrom;
        }
    },
    /**
    /**
     * Creates info windows for markers and clusters and stores them
     * within a global data structure for later reference
     */
    createCommunityPopups: function () {
        // Require Snazzy Maps now that Google Maps has loaded
        //let SnazzyInfoWindow = require('/assets/js/vendors/snazzy-info-window.js');
        const SnazzyInfoWindow = require('snazzy-info-window')

        // loop through all existing info windows and close
        for (const guid in CommunityMap._communityPopups) {
            var infoWindow = CommunityMap._communityPopups[guid];
            if (infoWindow !== CommunityMap._activeCommunityPopups) {
                infoWindow.close();
            }

        }

        // clear list of info windows
        CommunityMap._communityPopups = {};

        const HOST = "https://" + window.location.host;
        var closeIcon = HOST + '/patternlibrary/dist/assets/img/icons/close-icon-white.png';
        if (CommunityMap.IS_LOCALHOST === true) {
            closeIcon = HOST + "/assets/img/icons/close-icon-white.png";
        }


        // loop through all markers and create info windows for them indexed by guid
        CommunityMap._markers.forEach(function (marker) {
            var guid = marker.customInfo.nodeGuid;

            var homeTypes = (marker.customInfo.homeTypes != null && marker.customInfo.homeTypes.length > 0) ? marker.customInfo.homeTypes.join(" & ") : null;
            var homeTypesHtml = homeTypes == null ? "" : `<p class='info-window_label text-left'><span>${homeTypes}</span></p>`;
            var qmiLabel = LabelHelper.renderNumericLabel(marker.customInfo.qmicount, marker.customInfo.qmicount, "Quick Move-in!", "Quick MOve-ins!");
            var bedsLabel = LabelHelper.renderNumericLabel(marker.customInfo.bedmin, marker.customInfo.bedmax, "Bedroom", "Bedrooms");
            var bathsLabel = LabelHelper.renderNumericLabel(marker.customInfo.bathmin, marker.customInfo.bathmax, "Bathroom", "Bathrooms");
            // Conditional render half baths HTML only if we have ANY NON-ZERO half baths
            var halfBathsHtml = '';
            if (marker.customInfo.halfbathmin > 0 || marker.customInfo.halfbathmax > 0) {
                var halfBathsLabel = LabelHelper.renderNumericLabel(marker.customInfo.halfbathmin, marker.customInfo.halfbathmax, "Half-Bath", "Half-Baths", false);
                halfBathsHtml = `<span><b>${LabelHelper.renderNumericRange(marker.customInfo.halfbathmin, marker.customInfo.halfbathmax, false)}</b> ${halfBathsLabel}</span>`;
            }
            var storiesLabel = LabelHelper.renderNumericLabel(marker.customInfo.storymin, marker.customInfo.storymax, "Story", "Stories");
            var labelHtml = marker.customInfo.qmicount > 0
                ? `<p class='info-window_label text-left'><span class='qmi'>${marker.customInfo.qmicount} ${qmiLabel}</span></p>`
                : "";
            var labelHtmlMobile = marker.customInfo.qmicount > 0
                ? `<p class='info-window_label text-left show-for-small-only'><span class='qmi'>${marker.customInfo.qmicount} ${qmiLabel}</span></p>`
                : "";
            var tooltipIconUrl = window.IS_LOCALHOST
                ? "/assets/img/icons/tooltipinfo-icon.svg"
                : "/patternlibrary/dist/assets/img/icons/tooltipinfo-icon.svg";
            var cityStateHtml = marker.customInfo.city.length > 0 && marker.customInfo.state.length > 0
                ? `<p class='info-window_label text-left'>${marker.customInfo.city}, ${marker.customInfo.state}</p>`
                : "";
            // pattern library data will not contain pricing tooltip there fore construct html for that case
            // NOTE: need to have button markup immediately follow pricedFrom markup so there is no whitespace to alter spacing between the two
            var pricingTooltipHtml = CommunityMap.formatPriceHtml(marker, window, tooltipIconUrl);
            var infoWindowHtml = `
                                    <div class='fyh-search-info-window'>
                                    <a href='${marker.customInfo.url}' class='hide-for-small-only'>
                                        <img class='info-window_thumbnail' alt='${marker.customInfo.name} Thumbnail' src='${marker.customInfo.imageUrl}' />
                                        ${labelHtml}
                                        </a>
                                        <div class='fyh-search-info-window_details'>
                                            <a class='info-window_title text-left' href='${marker.customInfo.url}'>${marker.customInfo.name}</a>
                                            ${labelHtmlMobile}
                                            ${homeTypesHtml}
                                            ${cityStateHtml}
                                            <p class='info-window_label text-left'>
                                                ${pricingTooltipHtml}
                                            </p>
                                            <p class='info-window_label multiple-labels'><span><b>${LabelHelper.renderNumericRange(marker.customInfo.storymin, marker.customInfo.storymax)}</b> ${storiesLabel}</span><span><b>${LabelHelper.renderNumericRange(marker.customInfo.bedmin, marker.customInfo.bedmax)}</b> ${bedsLabel}</span></p>
                                            <p class='info-window_label multiple-labels'><span><b>${LabelHelper.renderNumericRange(marker.customInfo.bathmin, marker.customInfo.bathmax)}</b> ${bathsLabel}</span>${halfBathsHtml}
                                        </div>
                                    </div>
                                    `;

            var infoWin = new SnazzyInfoWindow({
                marker: marker,             // marker associated with this info window
                content: infoWindowHtml,
                placement: "top",           // where to place window with respect to marker
                wrapperClass: "fyh-search-info-window_wrapper-v2",
                openOnMarkerClick: false,    // do not open map on marker click since we are handling that manually (not via the library)
                closeOnMapClick: true,      // close window on outside clicks on map
                closeWhenOthersOpen: false,  // make sure only one window open at a time
                showCloseButton: true,      // show close button
                panOnOpen: true,            // make map pan to not clip window
                padding: "0px",
                border: false,              // remove border
                closeButtonMarkup: `<button type="button" class="fyh-search-info-window_close-btn info-window_close-btn si-close-button" aria-label="Close Info Window">
                                        <img class="info-window_close" src="` + closeIcon + `"/>
                                    </button>`,
                callbacks: {
                    beforeOpen: function () {
                        // manually close all other infowindows before opening this one
                        CommunityMap.closeCommunityPopups();
                    },
                    afterOpen: function () {
                        // Set the close click handler to close this window
                        var wrapper = $(this.getWrapper());
                        var infoWindow = this;
                        CommunityMap._activeCommunityPopups = this;
                        wrapper.find('.info-window_close-btn').on('click', function () {
                            infoWindow.close();
                            CommunityMap.closeCommunityPopups();
                        });

                        // init the lazy loaded pricing tooltip  
                        CommunityMap.initPricingTooltips($(wrapper.find("[data-tooltip]")));
                        window.lozadObserver();
                    }
                }

            });

            CommunityMap._communityPopups[guid] = infoWin;
        });

        // loop through all marker clusters with 2 or more markers and generate their info windows
        var clusters = CommunityMap._clusterer.clusters;
        for (var i = 0, l = clusters.length; i < l; i++) {
            var cluster = clusters[i];
            var markers = cluster.markers;
            var numMarkersInCluster = markers.length;

            // cluster case
            if (numMarkersInCluster > 1) {
                // build string compilation of all marker's guids delimited by "|"
                var markerGuids = "";
                markers.forEach(function (marker, index) {
                    markerGuids += marker.customInfo.nodeGuid;
                    if (index < numMarkersInCluster - 1) {
                        markerGuids += "|";
                    }
                });

                // BEGIN: Create html for popup
                var html = "<div class='fyh-search-info-window'>";
                var smallerHeightClass = numMarkersInCluster > 2 ? "smaller-height" : "";
                var hideInSmallerHeightClass = numMarkersInCluster > 2 ? "hide-in-smaller-height" : "";

                // Build html for each community inside cluster
                var markers = cluster.markers;
                markers.forEach(function (marker, index, markers) {
                    var communityTypes = (marker.customInfo.homeTypes != null && marker.customInfo.homeTypes.length > 0) ? marker.customInfo.homeTypes.join(" & ") : null;
                    var communityTypesHtml = communityTypes == null ? "" : `<p class='info-window_label text-left hide-for-small-only'><span>${communityTypes}</span></p>`;
                    var qmiLabel = LabelHelper.renderNumericLabel(marker.customInfo.qmicount, marker.customInfo.qmicount, "Quick Move-in!", "Quick MOve-ins!");
                    var bedsLabel = LabelHelper.renderNumericLabel(marker.customInfo.bedmin, marker.customInfo.bedmax, "Bedroom", "Bedrooms");
                    var bathsLabel = LabelHelper.renderNumericLabel(marker.customInfo.bathmin, marker.customInfo.bathmax, "Bathroom", "Bathrooms");
                    // Conditional render half baths HTML only if we have ANY NON-ZERO half baths
                    var halfBathsHtml = '';
                    if (marker.customInfo.halfbathmin > 0 || marker.customInfo.halfbathmax > 0) {
                        var halfBathsLabel = LabelHelper.renderNumericLabel(marker.customInfo.halfbathmin, marker.customInfo.halfbathmax, "Half-Bath", "Half-Baths", false);
                        halfBathsHtml = `<span><b>${LabelHelper.renderNumericRange(marker.customInfo.halfbathmin, marker.customInfo.halfbathmax, false)}</b> ${halfBathsLabel}</span>`;
                    }
                    var storiesLabel = LabelHelper.renderNumericLabel(marker.customInfo.storymin, marker.customInfo.storymax, "Story", "Stories");
                    var labelHtml = marker.customInfo.qmicount > 0
                        ? `<p class='info-window_label text-left ${hideInSmallerHeightClass}'><span class='qmi'>${marker.customInfo.qmicount} ${qmiLabel}</span></p>`
                        : "";
                    var tooltipIconUrl = window.IS_LOCALHOST
                        ? "/assets/img/icons/tooltipinfo-icon.svg"
                        : "/patternlibrary/dist/assets/img/icons/tooltipinfo-icon.svg";
                    // pattern library data will not contain pricing tooltip there fore construct html for that case
                    // NOTE: need to have button markup immediately follow pricedFrom markup so there is no whitespace to alter spacing between the two
                    var pricingTooltipHtml = CommunityMap.formatPriceHtml(marker, window, tooltipIconUrl);

                    // Build City and State Label
                    var cityAndStateArray = [];
                    if (marker.customInfo.city != null && marker.customInfo.city.length > 0) {
                        cityAndStateArray.push(marker.customInfo.city);
                    }
                    if (marker.customInfo.state != null && marker.customInfo.state.length > 0) {
                        cityAndStateArray.push(marker.customInfo.state);
                    }
                    var cityAndStateString = "";
                    if(cityAndStateArray.length > 0) {
                        cityAndStateString = cityAndStateArray.join(", ");
                    }

                    var infoWindowInnerHtml = `
                                                <div class='fyh-search-info-window_details ${smallerHeightClass}'>
                                                    <a class='info-window_title text-left' href='${marker.customInfo.url}'>${marker.customInfo.name}</a>
                                                    ${labelHtml}
                                                    ${communityTypesHtml}
                                                    <p class='info-window_label text-left ${hideInSmallerHeightClass}'>${cityAndStateString}</p>
                                                    <p class='info-window_label text-left'>
                                                        ${pricingTooltipHtml}
                                                    </p>
                                                    <p class='info-window_label multiple-labels hide-for-small-only'><span><b>${LabelHelper.renderNumericRange(marker.customInfo.storymin, marker.customInfo.storymax)}</b> ${storiesLabel}</span><span><b>${LabelHelper.renderNumericRange(marker.customInfo.bedmin, marker.customInfo.bedmax)}</b> ${bedsLabel}</span></p>
                                                    <p class='info-window_label multiple-labels hide-for-small-only'><span><b>${LabelHelper.renderNumericRange(marker.customInfo.bathmin, marker.customInfo.bathmax)}</b> ${bathsLabel}</span>${halfBathsHtml}</p>
                                                </div>
                                                `;
                    html += infoWindowInnerHtml;
                    if (index < markers.length - 1) {
                        html += "<hr />";
                    }

                });
                html += "</div>";
                // END: Create html for popup

                // Create popup for cluster
                var infoWin = new SnazzyInfoWindow({
                    position: cluster.position,             // locaiton to pin this to
                    map: CommunityMap._map,          // map to associate this info window with
                    content: html,
                    placement: "top",           // where to place window with respect to marker
                    wrapperClass: "fyh-search-info-window_wrapper-v2 fyh-search-info-window_wrapper-cluster",
                    openOnMarkerClick: false,    // open map on marker click
                    closeOnMapClick: true,      // close window on outside clicks on map
                    closeWhenOthersOpen: false,  // make sure only one window open at a time
                    showCloseButton: true,      // show close button
                    panOnOpen: true,            // make map pan to not clip window
                    padding: "0px",
                    border: false,              // remove border
                    closeButtonMarkup: `<button type="button" class="fyh-search-info-window_close-btn info-window_close-btn si-close-button" aria-label="Close Info Window">
                                        <img class="info-window_close" src="` + closeIcon + `"/>
                                    </button>`,
                    callbacks: {
                        beforeOpen: function () {
                            // manually close all other infowindows before opening this one
                            CommunityMap.closeCommunityPopups();
                        },
                        afterOpen: function () {
                            // Set the close click handler to close this window
                            var wrapper = $(this.getWrapper());
                            var infoWindow = this;
                            CommunityMap._activeCommunityPopups = this;
                            wrapper.find('.info-window_close-btn').on('click', function () {
                                infoWindow.close();
                                CommunityMap.closeCommunityPopups();
                            });

                            // init the lazy loaded pricing tooltip 
                            CommunityMap.initPricingTooltips($(wrapper.find("[data-tooltip]")));
                            window.lozadObserver();
                        }
                    }

                });

                CommunityMap._communityPopups[markerGuids] = infoWin;
            }
        }
    },
    /**
    * Closes the current InfoWindow on the map
    */
    closeCommunityPopups: function (keepActiveWindowOpen = false) {
        if (keepActiveWindowOpen === true) {
            // loop through all existing info windows and close all except for active one
            for (const guid in CommunityMap._communityPopups) {
                var infoWindow = CommunityMap._communityPopups[guid];
                if (infoWindow !== CommunityMap._activeCommunityPopups) {
                    infoWindow.close();
                }

            }
        }
        else {
            if (CommunityMap._activeCommunityPopups) {
                CommunityMap._activeCommunityPopups.close();
                CommunityMap._activeCommunityPopups = null;
            }
            // loop through all existing info windows and close and reset active window
            for (const guid in CommunityMap._communityPopups) {
                var infoWindow = CommunityMap._communityPopups[guid];
                infoWindow.close();
            }
            CommunityMap._activeCommunityPopups = null;
        }
    },
    /**
     * 
     * @param {MarkerClusterer} cluster cluster for which we want to fetch the icon for
     * @param {string} activeNodeGuid GUID representing the active communiy guid
     */
    getIconUrlForCluster(cluster, activeNodeGuid = null) {
        // Icon map for communities
        const HOST = "https://" + window.location.host;
        let ICON_BASE = HOST + "/patternlibrary/dist/assets/img/maps/";
        if (CommunityMap.IS_LOCALHOST === true) {
            ICON_BASE = HOST + "/assets/img/maps/";
        }

        var markers = cluster.markers;
        var clusterContainsActiveMarker = false;

        // find the marker that has the guid of interest
        var allClusterMarkerGuids = "";
        markers.forEach(function (marker, index) {
            var markerGuid = marker.customInfo.nodeGuid;

            // Compile "|" delimited list of guids this cluster represents
            allClusterMarkerGuids += markerGuid;
            if (index < markers.length - 1) {
                allClusterMarkerGuids += "|";
            }

            if (markerGuid === activeNodeGuid) {
                clusterContainsActiveMarker = true;
            }
        });

        let imgUrl = "";
        let clusterType = cluster.markers.length > 1 ? "multi" : "single";

        // if guid is null, reset icons to default
        if (activeNodeGuid == null) {
            imgUrl = `${ICON_BASE}clusters/${clusterType}-community-pin-green-2x.png?guid=${allClusterMarkerGuids}`;
        }
        else if (clusterContainsActiveMarker) {
            imgUrl = `${ICON_BASE}clusters/${clusterType}-community-pin-grey-2x.png?guid=${allClusterMarkerGuids}`;
        }
        else {
            imgUrl = `${ICON_BASE}clusters/${clusterType}-community-pin-green-2x.png?guid=${allClusterMarkerGuids}`;
        }
        return imgUrl;
    },
    /**
     * Use to mark specific marker/cluster markers as active an their associated labels
     * @param {*} guid 
     */
    highlightActiveCommunityIcon: function (guid) {
        // Icon map for communities
        const HOST = "https://" + window.location.host;
        var iconBase = HOST + "/patternlibrary/dist/assets/img/maps/";
        if (CommunityMap.IS_LOCALHOST === true) {
            iconBase = HOST + "/assets/img/maps/";
        }

        // Reset all labels and set active label by guid
        $(".fyh-search-icon-label-wrapper").removeClass("active");
        var associatedLabel = $(".fyh-search-icon-label-wrapper[class*='" + guid + "']:visible");
        associatedLabel.addClass("active");

        // Reset all active (grey icon markers) and set specified icon as active (icon src contains guid url parameter)
        // and reset all .active-icon-parents and .active-icon-grantparent
        var allIcons = $("img[src*='single-community-pin'], img[src*='multi-community-pin']").removeClass("active");
        $(".active-icon-grandparent").removeClass("active-icon-grandparent");
        $(".active-icon-parent").removeClass("active-icon-parent");
        var icon = $("img[src*='" + guid + "']:visible");
        icon.parent().addClass("active-icon-parent");
        icon.parent().parent().addClass("active-icon-grandparent");
        icon.addClass("active");

        // Reset all active clusters and set specified cluster as active (based on guid)
        $(".fyh-cluster").removeClass("active");
        $(".fyh-cluster[data-node-guid*='" + guid + "']:visible").addClass("active");

        // find the map marker with this guid and change its icon
        this._markers.forEach(function (marker) {
            var markerGuid = marker.customInfo.nodeGuid;

            // if guid is null, reset all marker icons
            if (guid == null) {
                marker.setIcon({
                    url: `${iconBase}single-community-pin-green-2x.png?guid=${markerGuid}`, // grey is the "active" icon
                    scaledSize: new google.maps.Size(28, 28), // scaled size
                });
            }
            else if (markerGuid === guid) {
                marker.setIcon({
                    url: `${iconBase}single-community-pin-grey-2x.png?guid=${markerGuid}`, // grey is the "active" icon
                    scaledSize: new google.maps.Size(28, 28), // scaled size
                });
            }
        });

        // handle case when marker lies within a cluster
        var clusters = this._clusterer.clusters;

        for (var i = 0, l = clusters.length; i < l; i++) {
            var cluster = clusters[i];
            var markers = cluster.markers;
            var clusterContainsActiveMarker = false;

            // find the marker that has the guid of interest
            var allClusterMarkerGuids = "";
            markers.forEach(function (marker, index) {
                var markerGuid = marker.customInfo.nodeGuid;

                // Compile "|" delimited list of guids this cluster represents
                allClusterMarkerGuids += markerGuid;
                if (index < markers.length - 1) {
                    allClusterMarkerGuids += "|";
                }

                if (markerGuid === guid) {
                    clusterContainsActiveMarker = true;
                }
            });

            // if guid is null, reset icons to default
            var clusterImgElem = $(`img[src*='${allClusterMarkerGuids}'`);

            if (clusterImgElem) {
                const CLUSTER_TYPE = cluster.markers.length > 1 ? "multi" : "single";

                // if guid is null, reset icons to default
                if (guid == null) {
                    clusterImgElem.attr("src", `${iconBase}clusters/${CLUSTER_TYPE}-community-pin-green-2x.png?guid=${allClusterMarkerGuids}`);
                }
                else if (clusterContainsActiveMarker) {
                    clusterImgElem.attr("src", `${iconBase}clusters/${CLUSTER_TYPE}-community-pin-grey-2x.png?guid=${allClusterMarkerGuids}`);
                }
                else {
                    clusterImgElem.attr("src", `${iconBase}clusters/${CLUSTER_TYPE}-community-pin-green-2x.png?guid=${allClusterMarkerGuids}`);
                }
            }
        }
    },
    /**
     * Sets event handlers for icon labels
     */
    initCommunityLabelHandlers: function () {
        var labels = $(".fyh-search-icon-label-wrapper");
        // set hover related event listeners here now that icon window is open in the DOM
        labels.off("mouseover").on("mouseover", function () {

            // Close the active info window
            CommunityMap.closeCommunityPopups();

            // Retrieve guid from label
            var iconLabelWrapper = $(this);
            var isClusterIconLabelWrapper = iconLabelWrapper.hasClass("cluster-label-wrapper");
            var classes = iconLabelWrapper[0].className.split(" ");
            var guid = classes.find(function (c) {
                return c.startsWith("data-node-guid-");
            });
            if (guid) {
                // strip away the data-note-guid- that is prepended
                // to the list of guids
                guid = guid.replace("data-node-guid-", "");

                let guids = guid.split("|");

                // at this point guid is either a delimited list of guids or a single guid
                // attempt to open the associate info window
                var infoWin = CommunityMap._communityPopups[guid];
                if (infoWin) {
                    // only open window if the number of communities reprsented is at or below the 
                    // threshold support for the infow window popup 
                    const MAX_COMMUNITIES_SUPPORTED_IN_POPUP = 4;
                    if (guids.length <= MAX_COMMUNITIES_SUPPORTED_IN_POPUP) {
                        infoWin.open();
                        CommunityMap._activeCommunityPopups = infoWin;
                    }
                }

                // since this is a cluster, node guid is delimited list of guids so use first
                // guid
                guid = guids.length > 0 ? guids[0] : null;
            }

            CommunityMap.highlightActiveCommunityIcon(guid);
        });

        // On label mouse out, reset any active markers
        labels.off("mouseout").on("mouseout", function () {
            CommunityMap.highlightActiveCommunityIcon(null);
        });

        // On label click, emulate marker icon click
        labels.off("click").on("click", function () {
            var classes = $(this)[0].className.split(" ");
            var guid = classes.find(function (c) {
                return c.startsWith("data-node-guid-");
            });
            if (guid) {
                // strip away the data-note-guid- that is prepended
                // to the list of guids
                guid = guid.replace("data-node-guid-", "");

                // since this is a cluster, node guid is delimited list of guids to use first
                // guid
                var guids = guid.split("|");
                guid = guids.length > 0 ? guids[0] : null;

                // Check if this guid is associated with a cluster currently
                var cluster = CommunityMap.getClusterByCommunityGuid(guid, CommunityMap._clusterer);

                // only if this is a cluster do we need to handle clicking on the label so that
                // it triggers the icon click (to cause cluster to drilldown)
                if (cluster) {
                    cluster.click();

                    // TODO: Determine how to dynamically trigger click event on cluster
                }
            }
        });
    },

    /**
     * 
     */
    initResultListingListeners: function () {

        // Add event listener to on hover on listing cards to highlight the active community
        $(".fyh-card-v2").off("mouseover").on("mouseover", function () {
            // Get guid from community guid to help search for the associated map marker to
            // mark pin as active
            var card = $(this);
            var guid = card.data("node-guid");

            CommunityMap.highlightActiveCommunityIcon(guid);
        });

        // Add event listener to on mouse out on listing cards reset all markers to use the 
        // default icon
        $(".fyh-card-v2").off("mouseout").on("mouseout", function () {
            CommunityMap.highlightActiveCommunityIcon(null);
        });
    },
    /**
     * Set attributes for the specified cluster
     * @param {*} clusterer 
     */
    setClusterAttributes: function (clusterer) {
        var clusters = clusterer.clusters;

        if (clusters == null) {
            return;
        }

        clusters.forEach(function (cluster) {
            var markers = cluster.markers;
            var numMarkersInCluster = markers.length;
            var markerGuids = "";

            if (numMarkersInCluster > 1) {
                // build string compilation of all marker's guids delimited by "|"
                markers.forEach(function (marker, index) {
                    markerGuids += marker.customInfo.nodeGuid;
                    if (index < numMarkersInCluster - 1) {
                        markerGuids += "|";
                    }
                });
            }
            else {
                markerGuids = markers[0].customInfo.nodeGuid;
            }

            // set customInfo of cluster icon to represent guids within it
            cluster["customInfo"] = {
                nodeGuids: markerGuids
            };
        });
    },
    /**
     * returns true or false if a marker lies within a cluster or not based on 
     * the marker's customInfo nodeGuid
     * @param {*} marker 
     * @param {*} clusterer 
     */
    isMarkerInACluster(marker, clusterer) {
        const CLUSTERS = clusterer.clusters;
        for (let i = 0; i < CLUSTERS.length; i++) {

            const CLUSTER_MARKERS = CLUSTERS[i].markers;

            for (let x = 0; x < CLUSTER_MARKERS.length; x++) {
                if (CLUSTER_MARKERS[x].customInfo.nodeGuid === marker.customInfo.nodeGuid && CLUSTER_MARKERS.length > 1) {
                    return true;
                }
            }
        }
        return false;
    },
    /**
     * Get marker cluster instance that contains the specified marker by guid
     * @param {*} guid 
     * @param {*} clusterer 
     */
    getClusterByCommunityGuid(guid, clusterer) {
        const CLUSTERS = clusterer.clusters;
        for (let i = 0; i < CLUSTERS.length; i++) {

            const CLUSTER_MARKERS = CLUSTERS[i].markers;

            for (let x = 0; x < CLUSTER_MARKERS.length; x++) {
                if (CLUSTER_MARKERS[x].customInfo.nodeGuid === guid && CLUSTER_MARKERS.length > 1) {
                    return CLUSTERS[i];
                }
            }
        }
        return null;
    },
    /**
     * Format Bathrooms string
     * @param {*} minValue min bathrooms
     * @param {*} maxValue max bathrooms
     */
    formatBathrooms: function (minValue, maxValue) {
        return CommunityMap.formatRangeString(minValue, maxValue, 'Bathroom', 'Bathrooms');
    },
    /**
     * Format Bedrooms string
     * @param {*} minValue min bedrooms
     * @param {*} maxValue max bedrooms
     */
    formatBedrooms: function (minValue, maxValue) {
        return CommunityMap.formatRangeString(minValue, maxValue, 'Bedroom', 'Bedrooms');
    },
    /**
     * Format a range label string
     * @param {*} minValue min value of range
     * @param {*} maxValue max value of range
     * @param {*} singularTerm singular term to use at end of label
     * @param {*} pluralTerm plural term to use at end of label
     */
    formatRangeString: function (minValue, maxValue, singularTerm, pluralTerm) {
        var minValueNum = Number(minValue);
        var maxValueNum = Number(maxValue);

        if (minValueNum == maxValueNum) {
            if (minValueNum == 1) {
                return (minValueNum + ' ' + singularTerm);
            } else {
                return (minValueNum + ' ' + pluralTerm);
            }
        } else {
            return (minValueNum + '-' + maxValueNum + ' ' + pluralTerm);
        }
    },
    /**
     * Initiazes the specified pricing tooltips or initializes all pricing toolips if none specified
     * @param {jQueryElement} elem Pricing Tooltip to initialize. Set to null to initialize all tooltips on page
     */
    initPricingTooltips(elem) {
        var tooltips = $(elem);


        // Determine if we need to target all unintialized tooltips (if they have non-empty data-tooltip
        // it means its already been initialized)
        if (elem == null) {
            const DEFAULT_PRICING_TOOLTIP_SELECTOR = ".pricing-tooltip[data-tooltip]";
            tooltips = $(DEFAULT_PRICING_TOOLTIP_SELECTOR);
        }

        // Unique case, since we are supporting DOM sorting of cards (in the FYH and Region listing), we must record the tooltip element's
        // title attribute within data-title since title gets erased upon init...restore this before reinitializing
        // again
        tooltips.each((index, elem) => {
           let dataTitle = $(elem).data("title")
           if (dataTitle != null) {
               $(elem).attr("title", dataTitle);
           }
        });

        // on hover remove the title temporary to remove browser native behavior of also displaying
        // the title in native browser popup
        tooltips.on("mouseover", (event) => {
            $(event.currentTarget).attr("title", "");
        });
        tooltips.on("mouseleave", (event) => {
            $(event.currentTarget).attr("title", $(event.currentTarget).data("title"));
        });

        // init the foundation tooltips
        tooltips.foundation();
            
       
    },

    /**
     * bindPricingTooltips
    */
    bindPricingTooltips(delay) {
        if (!delay) delay = 2000;
        setTimeout(function () {
            initPricingTooltips($(".pricing-tooltip[data-tooltip]"))
        }, delay);
    }


};
window.CommunityMap = CommunityMap;


export default CommunityMap;